import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import styled from "styled-components";
import ReactJson from "react-json-view";
import {
  Button,
  ButtonVariant,
  DateTableCell,
  ModalHeader,
  ReactTableComponent,
  Status,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import { useModalManager } from "../../../../context/ModalManager";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 20px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  column-gap: 16px;
  row-gap: 20px;
  padding: 18px;
  border: 1px solid #dbe3eb;
  border-radius: 8px;
  background: #fbfcfd;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled(Typography)`
  color: #6b7280;
`;

// No label-above-control, so it aligns to the row's inputs rather than its labels.
const CheckboxField = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: end;
  height: 36px;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }
`;

const controlCss = `
  box-sizing: border-box;
  height: 36px;
  border-radius: 5px;
  border: 1px solid #dbe3eb;
  padding: 0 10px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: #0e233e;
  background: #fff;
  outline: none;
  width: 100%;
  &:focus {
    border-color: #027aff;
    box-shadow: 0 0 0 3px rgba(2, 120, 255, 0.153);
  }
`;

const FilterSelect = styled.select`
  ${controlCss}
`;

const FilterInput = styled.input`
  ${controlCss}
`;

const Modal = styled.div`
  max-height: 90vh;
  overflow: auto;
  min-width: 640px;
`;

const ModalContent = styled.div`
  padding: 30px;
`;

const Url = styled.span`
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  font-size: 12px;
`;

const Muted = styled(Typography)`
  color: #6b7280;
`;

type FacetKey = "source" | "eventType" | "relatedEntityType";
type Facets = Record<FacetKey, string[]>;

const EMPTY_FACETS: Facets = {
  source: [],
  eventType: [],
  relatedEntityType: [],
};

interface FilterField {
  key: string;
  label: string;
  type: "select" | "text" | "number" | "datetime-local" | "checkbox";
  /** Static dropdown values. */
  options?: Array<{ value: string; label: string }>;
  /** Dropdown values read from the company's own records. */
  facet?: FacetKey;
  placeholder?: string;
}

// Always visible — the two an admin reaches for first.
const PRIMARY_FILTERS: FilterField[] = [
  {
    key: "direction",
    label: "Direction",
    type: "select",
    options: [
      { value: "in", label: "Incoming" },
      { value: "out", label: "Outgoing" },
    ],
  },
  {
    key: "status",
    label: "Status",
    type: "select",
    options: [
      { value: "succeeded", label: "Succeeded" },
      { value: "failed", label: "Failed" },
      { value: "ignored", label: "Ignored" },
    ],
  },
];

// Everything else on the record, behind the "More filters" toggle.
const SECONDARY_FILTERS: FilterField[] = [
  { key: "source", label: "Source", type: "select", facet: "source" },
  { key: "eventType", label: "Event type", type: "select", facet: "eventType" },
  {
    key: "relatedEntityType",
    label: "Entity type",
    type: "select",
    facet: "relatedEntityType",
  },
  { key: "hasError", label: "Has error", type: "checkbox" },
  { key: "eventId", label: "Event ID", type: "text" },
  { key: "externalEventId", label: "External event ID", type: "text" },
  { key: "relatedEntityId", label: "Entity ID", type: "text" },
  {
    key: "relatedEntityExternalId",
    label: "Entity external ID",
    type: "text",
  },
  { key: "url", label: "URL contains", type: "text" },
  { key: "errorMessage", label: "Error contains", type: "text" },
  { key: "responseStatus", label: "Response code", type: "number" },
  { key: "durationMsMin", label: "Min duration (ms)", type: "number" },
  { key: "durationMsMax", label: "Max duration (ms)", type: "number" },
  { key: "createdAtFrom", label: "From", type: "datetime-local" },
  { key: "createdAtTo", label: "To", type: "datetime-local" },
];

const DATE_KEYS = ["createdAtFrom", "createdAtTo"];

// List rows come back without requestBody/responseBody — those are only on the
// single-record response, which is what the row-click modal fetches.
interface WebhookRecordRow {
  _id: string;
  direction: string;
  status: string;
  source: string;
  eventId: string;
  eventType?: string | null;
  url?: string | null;
  responseStatus?: number | null;
  durationMs?: number | null;
  errorMessage?: string | null;
  createdAt: string;
}

// Bodies are stored as strings. Render JSON ones as a tree, leave anything else
// as the raw string.
const parseBody = (body: unknown) => {
  if (typeof body !== "string") return body;
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

// `datetime-local` has no timezone, so send an absolute instant instead of
// letting the server reinterpret the wall-clock time in its own zone.
const toQueryValue = (key: string, value: string) => {
  if (!DATE_KEYS.includes(key)) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
};

const toQuery = (filters: Record<string, string>) => {
  const query: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    const queryValue = value ? toQueryValue(key, value) : "";
    if (queryValue) query[key] = queryValue;
  });
  return query;
};

export const CompanyWebhooksTab: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const { showNotification } = useNotification();
  const { addModal, removeLastModal } = useModalManager();
  const [rows, setRows] = useState<WebhookRecordRow[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>(
    {}
  );
  const [facets, setFacets] = useState<Facets>(EMPTY_FACETS);
  const [showMore, setShowMore] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(false);

  const activeCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  // Debounced so typing in a text filter doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setAppliedFilters(filters), 400);
    return () => clearTimeout(t);
  }, [filters]);

  // Recomputed whenever the filters settle, so each dropdown only offers values
  // present in the current result set. The backend omits each facet's own filter
  // from its branch, so the field you just picked still lists its alternatives.
  useEffect(() => {
    let cancelled = false;
    api
      .getCompanyWebhookFilters(companyId, toQuery(appliedFilters))
      .then((data) => {
        if (!cancelled && data) setFacets({ ...EMPTY_FACETS, ...data });
      })
      // Dropdowns degrade to empty; the table still works.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [companyId, appliedFilters]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await api.listCompanyWebhookRecords(companyId, {
        page,
        limit,
        ...toQuery(appliedFilters),
      });
      setRows(payload?.docs ?? []);
      setTotalDocs(payload?.totalDocs ?? 0);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Could not load webhooks"),
        NotificationTypes.WARNING
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, page, limit, appliedFilters, showNotification]);

  useEffect(() => {
    void loadRecords();
  }, [loadRecords]);

  const setFilter = useCallback((key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  const renderField = useCallback(
    (field: FilterField) => {
      const value = filters[field.key] ?? "";

      if (field.type === "checkbox") {
        return (
          <CheckboxField key={field.key}>
            <input
              type="checkbox"
              checked={value === "true"}
              onChange={(e) =>
                setFilter(field.key, e.target.checked ? "true" : "")
              }
            />
            <FieldLabel variant={TypographyVariant.BODY7}>
              {field.label}
            </FieldLabel>
          </CheckboxField>
        );
      }

      let options =
        field.options ??
        (field.facet
          ? facets[field.facet].map((v) => ({ value: v, label: v }))
          : []);
      // Another filter can narrow this one's options past the current selection
      // (pick source=sumsub, then a status it never had). Keep it listed so the
      // control doesn't render blank while still being applied.
      if (value && !options.some((o) => o.value === value)) {
        options = [...options, { value, label: value }];
      }

      return (
        <Field key={field.key}>
          <FieldLabel variant={TypographyVariant.BODY7}>
            {field.label}
          </FieldLabel>
          {field.type === "select" ? (
            <FilterSelect
              value={value}
              onChange={(e) => setFilter(field.key, e.target.value)}
            >
              <option value="">All</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FilterSelect>
          ) : (
            <FilterInput
              type={field.type}
              value={value}
              placeholder={field.placeholder}
              onChange={(e) => setFilter(field.key, e.target.value)}
            />
          )}
        </Field>
      );
    },
    [filters, facets, setFilter]
  );

  const onRowClick = useCallback(
    async (row: WebhookRecordRow) => {
      try {
        const record = await api.getCompanyWebhookRecord(companyId, row._id);
        addModal(
          <Modal>
            <ModalHeader
              title={`Webhook — ${record.direction} · ${record.status}`}
              handleClose={removeLastModal}
            />
            <ModalContent>
              <ReactJson
                src={{
                  ...record,
                  requestBody: parseBody(record.requestBody),
                  responseBody: parseBody(record.responseBody),
                }}
                enableClipboard
                name="webhook"
                collapsed={2}
                displayDataTypes={false}
              />
            </ModalContent>
          </Modal>
        );
      } catch (e: unknown) {
        showNotification(
          getApiErrorMessage(e, "Could not load webhook record"),
          NotificationTypes.WARNING
        );
      }
    },
    [companyId, addModal, removeLastModal, showNotification]
  );

  const columns = useMemo<Array<ColumnDef<WebhookRecordRow, any>>>(
    () => [
      {
        header: "Created",
        accessorKey: "createdAt",
        cell: ({ getValue }) =>
          getValue() ? <DateTableCell date={getValue()} /> : "—",
      },
      {
        header: "Direction",
        accessorKey: "direction",
        cell: ({ getValue }) => (getValue() === "in" ? "Incoming" : "Outgoing"),
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => <Status status={getValue()} />,
      },
      {
        header: "Source",
        accessorKey: "source",
        cell: ({ getValue }) => getValue() ?? "—",
      },
      {
        header: "Event",
        accessorKey: "eventType",
        cell: ({ getValue }) => getValue() ?? "—",
      },
      {
        header: "URL",
        accessorKey: "url",
        cell: ({ getValue }) =>
          getValue() ? <Url title={getValue()}>{getValue()}</Url> : "—",
      },
      {
        header: "Code",
        accessorKey: "responseStatus",
        cell: ({ getValue }) => getValue() ?? "—",
      },
      {
        header: "Duration",
        accessorKey: "durationMs",
        cell: ({ getValue }) => (getValue() != null ? `${getValue()} ms` : "—"),
      },
    ],
    []
  );

  return (
    <Wrap>
      <FilterBar>
        {PRIMARY_FILTERS.map(renderField)}
        <Button
          variant={ButtonVariant.OUTLINED}
          type="button"
          onClick={() => setShowMore((v) => !v)}
        >
          {showMore ? "Fewer filters" : "More filters"}
          {activeCount ? ` (${activeCount})` : ""}
        </Button>
        {activeCount > 0 && (
          <Button
            variant={ButtonVariant.OUTLINED}
            type="button"
            onClick={clearFilters}
          >
            Clear all
          </Button>
        )}
      </FilterBar>

      {showMore && (
        <FilterGrid>{SECONDARY_FILTERS.map(renderField)}</FilterGrid>
      )}

      {loading && rows.length === 0 ? (
        <Typography variant={TypographyVariant.BODY1}>
          Loading webhooks...
        </Typography>
      ) : (
        <>
          <Muted variant={TypographyVariant.BODY7}>
            {totalDocs} record{totalDocs === 1 ? "" : "s"}
            {activeCount ? " matching the current filters" : ""}
          </Muted>
          <ReactTableComponent
            columns={columns}
            data={rows}
            onRowClick={onRowClick}
            withPagination={{
              totalDocs,
              page,
              limit,
              onPaginationChange: (nextPage, nextLimit) => {
                setPage(nextPage);
                setLimit(nextLimit);
              },
            }}
          />
        </>
      )}
    </Wrap>
  );
};

export default CompanyWebhooksTab;
